<template>
  <div class="cart">
    <div class="container">
      <div class="cart-heading">
        <div class="cart-h1">Review Cart</div>
        <div class="cart-actions">
          <a href="#" v-b-modal.emptyCartModal v-show="$store.state.cart.commerceItemCount > 0">
            <span class="fa fa-trash"></span>
            <span class="d-none d-lg-inline"> Empty cart</span>
          </a>
        </div>
      </div>
      <error-container v-bind:type="'general'" />
      <b-modal id="emptyCartModal" ref="emptyCartModal" hide-footer static>
        <template slot="modal-title">
          <span data-message-type="alert">Empty your cart?</span>
        </template>
        <div class="mb-3">This will remove all items from your cart.</div>
        <div class="modal-footer">
          <button class="btn btn-secondary mr-3" @click="$refs.emptyCartModal.hide()">Cancel</button>
          <button class="btn btn-primary" @click.prevent="emptyCart">Empty</button>
        </div>
      </b-modal>

      <div class="row">
        <div class="col-lg-9">
          <cart-sparkle v-bind:cartItems="cartItems"></cart-sparkle>
          <div class="reservation-display">
            <reservation-display :showExpiration="true" />
          </div>

          <div
            v-if="upsells.cartProductRecommendations.length && this.showFeaturedProducts"
            class="featured-products-section"
          >
            <h5 class="cart-h5">{{upsells.cartProductRecommendationsDisplayName}}</h5>
            <div class="panel featured-products-panel">
              <product-collection-horizontal
                :listname="upsells.cartProductRecommendationsDisplayName"
                :products="upsells.cartProductRecommendations"
                size="small"
                reco="default"
                :hidePreferences="true"
              ></product-collection-horizontal>
            </div>
            <div @click="hideFeaturedProducts" class="featured-products-hide-button fa fa-times"></div>
          </div>

          <div class="cart-notices">
            <notice-representative-product v-if="$store.state.cart.containsBestAvailableItem" notice-bcc-id="covid_representative_product_notice" class="cart-notice-container"/>

            <!-- COVID-19 / CTH-1612 Added the next 6 lines -->
            <div class="cart-notice-container"
              v-if="!cartSubstitutionsEnabled && cartSubstitutionsDisclaimer">
              <div class="covid-info__container">
                  <div class="covid-info__heading">Substitutions</div>
                  <p class="covid-info__p">{{cartSubstitutionsDisclaimer}}</p>
              </div>
            </div>
          </div>

          <template v-if="showSubstitutionsToggle">
            <b-tooltip
              target="tooltip-substitutions"
              triggers="hover focus click blur"
              container="curbsideCheckout">
              Our goal is never to be out of stock on any item. However, if an item is out of stock we will make every effort to contact you and substitute with an item as close to the price and size of the original item.
            </b-tooltip>

            <div class="cart-substitutions">
              <input id="subs-preference-all"
                class="cart-substitutions__checkbox"
                type="checkbox"
                v-model="cartSubstitutions"
                @change="toggleCartSubstitutions"
              />
              <label for="subs-preference-all" class="cart-substitutions__label">
                Allow substitutions
              </label>
              <span tabindex="0" id="tooltip-substitutions">
                <span class="fa fa-question-circle"></span>
              </span>
            </div>
          </template>

          <section class="cart-contents mb-3">
            <div
              v-if="$store.state.cart.commerceItemCount < 1 && !$store.state.cart.isFetchingCart"
              class="cart-empty-message"
            >Your cart is empty.</div>
            <div
              v-if="$store.state.cart.commerceItemCount < 1 && $store.state.cart.isFetchingCart"
              class="cart-empty-message"
            >Fetching your cart, please wait...</div>
            <section class="category-section">
              <div v-if="$store.state.cart.commerceItemCount > 0">
                <div
                  v-for="(category, categoryIndex) in cartItems"
                  v-bind:key="category.id"
                  v-if="category.items.length > 0"
                >
                  <h2 class="cart-category-heading" v-html="category.shortDisplayName"></h2>
                  <div class="panel cart-category-list">
                    <cart-item
                      v-for="(item, itemIndex) in category.items"
                      v-bind:cartItem="item"
                      v-bind:categoryIndex="categoryIndex"
                      v-bind:itemIndex="itemIndex"
                      v-bind:options="{displayType: 'cart'}"
                      v-bind:key="item.id"
                      listname="Review Cart"
                      :position="itemIndex+1"
                      v-if="cartItems"
                    ></cart-item>
                  </div>
                </div>
              </div>
              <div v-if="$store.state.cart.containsBestAvailableItem" class="covid-info__container best-available" v-html="$store.state.cart.bestAvailableDisclaimer">
              </div>
            </section>
          </section>
          <div v-if="upsells.frequentlyPurchasedProductRecommendations.length">
            <h5 class="cart-h5">Frequently purchased</h5>
            <div class="panel">
              <product-collection-horizontal
                listname="Frequently purchased"
                :products="upsells.frequentlyPurchasedProductRecommendations"
                :size="upsellProductSize"
                reco="default"
              ></product-collection-horizontal>
            </div>
          </div>

          <div v-if="upsells.shoppingListRecommendations.length">
            <h5 class="cart-h5">In your shopping list</h5>
            <div class="panel">
              <product-collection-horizontal
                listname="In your shopping list"
                :products="upsells.shoppingListRecommendations"
                :size="upsellProductSize"
                reco="default"
              ></product-collection-horizontal>
            </div>
          </div>

          <!-- only show this in small or lower sizes, otherwise its in the sidebar -->
          <div v-if="upsells.forgottenProductRecommendations.length" class="d-block d-md-none">
            <h5 class="cart-h5">Sponsored products</h5>
            <div class="panel">
              <product-collection-horizontal
                listname="Sponsored products"
                :products="upsells.forgottenProductRecommendations"
                :size="upsellProductSize"
                reco="default"
              ></product-collection-horizontal>
            </div>
          </div>
        </div>

        <div class="col-lg-3 d-none d-md-block">
          <!-- Right bar -->
          <section v-if="$store.state.cart.priceInfo" class="cart-total-section">
            <cart-total v-bind:cartTotalCardInfo="cartTotalCardInfo"></cart-total>
          </section>

          <section v-if="upsells.forgottenProductRecommendations.length" class="panel">
            <product-collection-sidebar
              :products="upsells.forgottenProductRecommendations"
              :title="'Sponsored products'"
              reco="default"
            ></product-collection-sidebar>
          </section>
        </div>
        <!-- End right bar -->
      </div>
      <!-- ./row -->
    </div>
    <!-- ./container -->


    <!-- Used to prompt a user to confirm a message before proceeding to checkout -->
    <!-- Will only show if proceedToCheckoutMessage computed value exists -->
    <b-modal
      id="proceed-to-checkout-modal"
      ref="proceedToCheckoutModal"
      centered
      static
      size="sm"
      v-if="!!proceedToCheckoutMessage"
    >
      {{ proceedToCheckoutMessage }}
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          size="sm"
          class="proceed-to-checkout-modal__cancel-btn"
          @click="cancel()"
        >
          Close
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="proceed-to-checkout-modal__continue-btn"
          @click="proceedToCheckout"
        >
          Continue
        </b-button>
      </template>
    </b-modal>

    <!-- Order history cartridge for home page. This would be great to leave in for testing and build purposes please -->
    <!-- <order-history-cartridge></order-history-cartridge> -->
    <!--  End of order history cartridge -->

    <sticky-footer
      class="sticky-footer"
      v-bind:isDelivery="shouldShowFooterGratuity"
      v-bind:cartTotalCardInfo="cartTotalCardInfo"
    ></sticky-footer>
    <!-- v-bind:submitButtonText="cartTotalButtonProp.submitButtonText"  -->
  </div>
  <!-- ./cart -->
</template>

<script>
import { mapGetters } from "vuex";

import components from "../../../shared/components.js";

import { checkout as checkoutMixin } from '../../../shared/components/mixins/checkout'

export default {
  name: "Cart",
  // Include the checkoutValidation method
  mixins: [
    checkoutMixin
  ],
  components: {
    "cart-total": components.CartTotalCard,
    "sticky-footer": components.NavigationFooter,
    "reservation-display": components.ReservationDisplay,
    "product-collection-sidebar": components.ProductCollectionSidebar,
    "product-collection-horizontal": components.ProductCollectionHorizontal,
    "error-container": components.Errors,
    "cart-item": components.CartItem,
    "cart-sparkle": components.CartSparkle,
    "notice-representative-product": components.NoticeRepresentativeProduct
  },
  data() {
    return {
      showFeaturedProducts: true,
      cartTotalCardInfo: {
        showSubTotal: true,
        showPersonalShoppersFee: true,
        showSubmitBtn: true,
        showPromoCodeInput: true,
        showDesclaimerText: false,
        showAllSavingsSection: true,
        showViewMyOrder: false,
        showTaxAmount: false,
        showPriceTotal: false,
        showTotalSavingsSection: true,
        gratuityText: "Driver Tip",
        submitButtonText: "Checkout Now",
        btnRoutePath: "/checkout"
      },
      /** @todo: update once API is ready for isDelivery boolean */
      isDelivery: false
    };
  },
  computed: {
    proceedToCheckoutMessage() {
      // If we ever need to prompt the user with a confirmation message before
      // going to checkout, get/set the message here
      // ex. return _.get(window, "heb.data.message.limitedAddonsPerOrder.body", false);
      return false;
    },
    isLarge() {
      return window.heb.screen.isLarge;
    },
    isColdUser() {
      return window.heb.data.user.isGuest;
    },
    upsellProductSize() {
      return window.heb.screen.isSmall ? "small" : "normal";
    },
    shouldShowFooterGratuity() {
      return this.isDelivery;
    },
    upsells() {
      return this.$store.state.profile.upsells;
    },
    showSubstitutionsToggle() {
      return this.cartSubstitutionsEnabled &&
        this.cartItems.some(category => category.items.some(item => item.substitutionPreferenceEnabled));
    },
    ...mapGetters({
      cartItems: "cart/cartItems",
      cartSubstitutions: "cart/cartSubstitutions",
      cartSubstitutionsEnabled: 'cart/cartSubstitutionsEnabled',
      cartSubstitutionsDisclaimer: 'cart/cartSubstitutionsDisclaimer',
      reservationId: "reservation/reservationId"
    })
  },
  methods: {
    hideFeaturedProducts: function() {
      this.showFeaturedProducts = false;
    },
    emptyCart: function() {
      this.$store.dispatch("cart/DELETE_CART");
      this.$refs.emptyCartModal.hide();
    },
    toggleCartSubstitutions() {
      this.$store.commit("cart/TOGGLE_CART_SUBSTITUTIONS");
    },
    fetchUpsells: function() {
      this.$store.dispatch(
        "profile/FETCH_UPSELL",
        {name: "cartProductRecommendations"}
      );

      this.$store.dispatch(
        "profile/FETCH_UPSELL",
        {name: "forgottenProductRecommendations"}
      );

      this.$store.dispatch(
        "profile/FETCH_UPSELL",
        {name: "shoppingListRecommendations"}
      );

      if (!this.isColdUser) {
        this.$store.dispatch(
          "profile/FETCH_UPSELL",
          {name: "frequentlyPurchasedProductRecommendations"}
        );
      }
    }
  },
  mounted: function() {
    this.$store.commit("errors/CLEAR_ERRORS");
    this.$store.commit("cart/SET_FETCHING_CART", true);
    this.$store.dispatch("cart/LOAD_CART", false);
    this.fetchUpsells();

    document.getElementById("header").style.display = "block";
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../../../shared/assets/lib";

.cart {
  padding-bottom: 136px;
}

.cart-heading {
  padding-left: 0;
}

.cart-actions {
  font-weight: 600;
  font-size: 14px;
}

.reservation-display {
  margin-bottom: 32px;
}

.panel {
  background: #fff;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
  padding: 16px;
  margin-bottom: 8px;
}

.featured-products-section {
  position: relative;
  .featured-products-hide-button {
    position: absolute;
    right: 3px;
    top: 3px;
  }
}

.featured-products-panel {
  margin-bottom: 16px;
}

.cart-empty-message {
  text-align: center;
  padding: 64px;
}

.cart-notices {
  margin-bottom: 32px;
}

.cart-substitutions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;

  .fa-question-circle {
    color: #adadad;
    margin-left: 8px;
  }
}

.cart-substitutions__label {
  display: flex;
  align-items: center;
  margin-bottom: 0 !important;
}

.cart-substitutions__checkbox {
  margin-right: 8px;
}

.cart-category-heading {
  font-size: 19px;
  font-weight: 800;
  margin: 0 0 16px;
  padding: 0;
}

.panel.cart-category-list {
  padding: 0 16px;
  margin-bottom: 32px;
}

.cart-contents {
  a {
    color: $color-text-standard;
  }

  a:hover {
    text-decoration: underline;
  }
}

// End .cart-contents, I'll move this to a component later

// Start Checkout Sticky Footer
.sticky-footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: z-index("checkout-mobile-drawer");
}

// End Checkout Sticky Footer

// @media screen and (min-width: $breakpoint-tablet){
@media screen and (min-width: $breakpoint-tablet) {
  .cart-total-section {
    margin-bottom: 24px;
  }
}

// @media screen and (min-width: $breakpoint-desktop){

@media screen and (min-width: $breakpoint-desktop) {
  .cart-h1 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 0;
  }

  .cart-h5 {
    font-weight: bold;
  }
}

  // COVID-19 / CTH-1612 Added style for alert
  .best-available {
    @media screen and (min-width: $breakpoint-desktop){
      display: none;
    }
  }

  .covid-info__container {
    background: linear-gradient(0deg, rgba(252, 209, 49, 0.05), rgba(252, 209, 49, 0.05)), #FFFFFF;
    /* Pineapple (#FCD131) */
    border: 1px solid #FCD131;
    border-radius: 24px;
    text-align: center;
    box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.05);
    padding: 14px;
    margin-bottom: 1rem;
  }

  .covid-info__heading {
    font-weight: 800;
    font-size: 20px;
    line-height: 24px;
    margin-bottom: 4px;
  }

  .covid-info__p {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
  }

// Adding style rule for b-modal cancel button
.proceed-to-checkout-modal__cancel-btn {
  border: 0px solid transparent;
}

@media screen and (min-width: $breakpoint-tablet) {
  .cart-notice-container{
    padding: 0 10%;
  }
}

</style>

<docs>
  When the cart component is mounted, it sends off a request to get the latest cart information.
</docs>
